import { useEffect, useState } from "react";
import { listOpsDeals } from "@/lib/opsPaperworkClient";
import { DEALS_PICKER_PAGE_SIZE } from "@/lib/ops/opsDealsListQuery";
import {
  filterOpsDealPicks,
  opsDealPicksFromList,
  type OpsDealPick,
} from "@/lib/service/opsDealPicker";
import { parseOpsDealId } from "@/lib/service/serviceJobKind";
import { inputClass } from "./serviceShared";

export function ServiceOriginalDealField(props: {
  selected: OpsDealPick | null;
  disabled?: boolean;
  onPick: (hit: OpsDealPick) => void;
  onClear: () => void;
}) {
  const [q, setQ] = useState("");
  const [deals, setDeals] = useState<OpsDealPick[]>([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    void listOpsDeals({ status: "active", page_size: DEALS_PICKER_PAGE_SIZE }).then((r) => {
      if (cancelled) return;
      setBusy(false);
      if (!r.ok) {
        setDeals([]);
        return;
      }
      setDeals(opsDealPicksFromList(r.data.deals));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  function applyTypedUuid() {
    const id = parseOpsDealId(q);
    if (!id) return;
    const known = deals.find((d) => d.id === id);
    props.onPick(known ?? { id, label: `${id.slice(0, 8)}…` });
    setQ("");
    setOpen(false);
  }

  const hits = filterOpsDealPicks(deals, q);

  return (
    <div className="relative">
      {props.selected ? (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-700">
          <span className="min-w-0 truncate">{props.selected.label}</span>
          <button
            type="button"
            className="shrink-0 text-xs text-indigo-700 underline dark:text-indigo-300"
            disabled={props.disabled}
            onClick={props.onClear}
          >
            Change
          </button>
        </div>
      ) : (
        <input
          className={inputClass}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            applyTypedUuid();
            window.setTimeout(() => setOpen(false), 150);
          }}
          placeholder="Search deal by name (follow-on / warranty, not only the original sale)"
          disabled={props.disabled}
        />
      )}
      {!props.selected && open && hits.length > 0 ? (
        <ul className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white text-sm shadow dark:border-zinc-700 dark:bg-zinc-900">
          {hits.map((h) => (
            <li key={h.id}>
              <button
                type="button"
                className="block w-full px-2 py-1.5 text-left hover:bg-gray-50 dark:hover:bg-zinc-800"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  props.onPick(h);
                  setQ("");
                  setOpen(false);
                }}
              >
                <span className="font-medium">{h.label}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {!props.selected && busy ? (
        <p className="mt-0.5 text-[11px] text-gray-500">Loading deals…</p>
      ) : null}
    </div>
  );
}
